import { COLOR_TOKEN } from 'src/components/color-token';
/* eslint-disable react/display-name */
import { styled } from '@volkswagen-onehub/components-core';
import React, { useState } from 'react';
import { Wrapper } from 'src/feature-app/NewMap/components/CommonStyledComponents';
import {
	MarkerDimensions,
	useMapGeneralController,
} from 'src/feature-app/NewMap/MapGeneralController';
import { DealersData, Installation, Points } from 'src/types';

const MarkerSvgContainer = styled.div<{ markerDimensions: MarkerDimensions }>`
	width: ${props => `${props.markerDimensions.width}px`};
	height: ${props => `${props.markerDimensions.height}px`};
	& svg {
		width: 100%;
		height: 100%;
	}
`;

const Dot = styled.div<{ greyCard: boolean }>`
	height: 4px;
	width: 4px;
	background-color: ${props =>
		props.greyCard
			? COLOR_TOKEN.main.content.tertiary
			: COLOR_TOKEN.main.accent.nbab.default};
	border-radius: 100%;
	display: inline;
	position: absolute;
	bottom: 0;
	left: 44%;
`;

interface DealerMarkerProps {
	onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	lat: number;
	lng: number;
	isGrey: boolean;
	isActive: boolean;
	isHover: boolean;
	isFavourite: boolean;
}

export const isDealerGrey = (
	carPickupSelected: boolean,
	replacementCarSelected: boolean,
	point: Points,
) =>
	pickupSelectedAndDealerHasIt(carPickupSelected, point) ||
	replacementCarSelectedAndDealerHasIt(replacementCarSelected, point);

export const isDealerActive = (selectedPoint: Points, point: Points) =>
	selectedPoint?.properties?.dealer?.kvps === point?.properties?.dealer?.kvps;

export const isDealerFavourite = (favourite: DealersData, point: Points) =>
	favourite?.kvps === point?.properties?.dealer?.kvps;

export const isInstallationFavourite = (
	favourite: Installation,
	point: Points,
) => favourite?.KVPSCode__c === point?.properties?.dealer?.kvps;

export const isDealerHovered = (hoverPoint: Points, point: Points) =>
	hoverPoint?.properties?.dealer?.kvps === point?.properties?.dealer?.kvps;

const pickupSelectedAndDealerHasIt = (
	carPickupSelected: boolean,
	point: Points,
) =>
	carPickupSelected &&
	!point?.properties?.dealer?.dmsInfo?.pickupCarServiceAvailabe;
const replacementCarSelectedAndDealerHasIt = (
	replacementCarSelected: boolean,
	point: Points,
) =>
	replacementCarSelected &&
	!point?.properties?.dealer?.dmsInfo?.replacementCarServiceAvailable;

export const DealerMarker = React.memo(
	({
		onClick,
		isGrey,
		isActive,
		isHover,
		isFavourite,
	}: DealerMarkerProps) => {
		const markerDimensions = useMapGeneralController().markerDimensions;
		const [mouseEntered, setMouseEntered] = useState(false);

		return (
			<Wrapper
				onClick={onClick}
				onMouseEnter={e => {
					e.preventDefault();
					setMouseEntered(true);
				}}
				onMouseLeave={e => {
					e.preventDefault();
					setMouseEntered(false);
				}}
				onTop={isActive || isHover || mouseEntered ? true : false}
			>
				<MarkerSvgContainer markerDimensions={markerDimensions}>
					{isGrey ? (
						<svg
							className={`marker ${
								isActive || isHover || mouseEntered
									? 'active'
									: ''
							}`}
							fill={COLOR_TOKEN.main.button.primary.disabled}
							viewBox="0 0 80 110"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path d="M40 110C40 110 80 65.0635 80 40.7095C80 16.3554 64.127 0 40 0C15.873 0 0 16.3554 0 40.7095C0 65.0635 40 110 40 110Z" />
						</svg>
					) : isFavourite ? (
						<svg
							className={`favourite-dealer-marker ${
								isActive || isHover || mouseEntered
									? 'active'
									: ''
							}`}
							fill="red"
							viewBox="0 0 80 110"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path d="M40 110C40 110 80 65.0635 80 40.7095C80 16.3554 64.127 0 40 0C15.873 0 0 16.3554 0 40.7095C0 65.0635 40 110 40 110Z" />
							<path
								d="M40.1048 61.2131C31.7623 61.2131 16.3298 41.9456 13.0798 35.9381C11.5319 32.8117 11.2671 29.2042 12.3418 25.8853C13.4165 22.5664 15.7456 19.7988 18.8323 18.1731C21.5618 16.8116 24.651 16.3447 27.661 16.8388C30.671 17.333 33.4487 18.7629 35.5998 20.9256L40.1048 25.4306L44.3998 20.9256C46.5511 18.7633 49.329 17.3338 52.339 16.8402C55.349 16.3465 58.438 16.8138 61.1673 18.1756C64.2543 19.8009 66.584 22.5683 67.6592 25.8872C68.7344 29.2062 68.4699 32.8139 66.9223 35.9406C63.6698 41.9456 48.4148 61.2131 40.1048 61.2131Z"
								fill="white"
							/>
						</svg>
					) : (
						<svg
							className={`dealer-marker ${
								isActive || isHover || mouseEntered
									? 'active'
									: ''
							}`}
							fill={
								isActive || isHover || mouseEntered
									? COLOR_TOKEN.main.button.primary.hover
									: COLOR_TOKEN.main.button.primary.default
							}
							viewBox="0 0 80 110"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path d="M40 110C40 110 80 65.0635 80 40.7095C80 16.3554 64.127 0 40 0C15.873 0 0 16.3554 0 40.7095C0 65.0635 40 110 40 110Z" />
							<path
								fillRule="evenodd"
								clipRule="evenodd"
								d="M38.6355 22.2168H56.2022C57.5369 22.2168 58.6189 23.2985 58.6189 24.6333V50.6833C58.6189 52.0181 57.5369 53.1001 56.2022 53.1001H50.5022L51.0022 55.6333C51.1292 56.2708 50.972 56.9321 50.5717 57.4443C50.1717 57.9563 49.5682 58.2688 48.9189 58.3001C44.7022 58.5001 40.4522 58.6001 36.2189 58.6001C31.9854 58.6001 27.7354 58.5001 23.5189 58.3001C22.8696 58.2688 22.2662 57.9563 21.866 57.4443C21.4658 56.9321 21.3087 56.2708 21.4356 55.6333L22.7189 49.2833C23.0744 47.4838 24.3353 45.9961 26.0522 45.3501L29.1854 44.1668L30.0022 46.1833L26.8354 47.3501C25.8352 47.7331 25.0997 48.6006 24.8855 49.6501L23.6189 56.0668C24.8522 56.2001 26.0854 56.2001 27.3354 56.2001L27.8354 52.3501L30.0022 52.6501L29.6022 56.3168C33.9854 56.4168 38.3855 56.4168 42.769 56.3168L42.2854 52.6501L44.4855 52.3501L45.0022 56.2333L48.9022 56.1668L47.6355 49.7168C47.4275 48.6643 46.6897 47.7943 45.6855 47.4168L42.5522 46.2501L43.3354 44.1668L46.3355 45.2833C48.0602 45.9183 49.3254 47.4113 49.6689 49.2168L50.0022 50.8833H56.2022C56.3062 50.8746 56.386 50.7876 56.3855 50.6833V24.6333C56.3855 24.532 56.3034 24.45 56.2022 24.45H38.6355C38.5842 24.4453 38.5332 24.4625 38.495 24.4975C38.457 24.5323 38.4354 24.5818 38.4354 24.6333V30.8333C42.1579 32.0153 44.3405 35.8661 43.4425 39.6668C42.5445 43.4678 38.8692 45.9343 35.0115 45.3253C31.1537 44.7161 28.4172 41.2371 28.7339 37.3446C29.0504 33.4518 32.3134 30.4608 36.2189 30.4833V24.6333C36.2189 23.2985 37.301 22.2168 38.6355 22.2168ZM36.2077 42.9001C39.0222 42.9061 41.3097 40.6311 41.3189 37.8168C41.3189 34.9973 39.0382 32.7093 36.2189 32.7001C33.4044 32.7001 31.1219 34.9801 31.1189 37.7946C31.1159 40.6091 33.3934 42.8938 36.2077 42.9001Z"
								fill={
									isActive || isHover || mouseEntered
										? COLOR_TOKEN.main.content.buttonHover
										: COLOR_TOKEN.main.content.inverted
								}
							/>
							<path
								d="M45.834 28.1166H53.3007V30.3333H45.834V28.1166Z"
								fill={
									isActive || isHover || mouseEntered
										? COLOR_TOKEN.main.content.buttonHover
										: COLOR_TOKEN.main.content.inverted
								}
							/>
							<path
								d="M45.834 33.1833H53.3007V35.4H45.834V33.1833Z"
								fill={
									isActive || isHover || mouseEntered
										? COLOR_TOKEN.main.content.buttonHover
										: COLOR_TOKEN.main.content.inverted
								}
							/>
						</svg>
					)}
				</MarkerSvgContainer>

				{isActive ? <Dot greyCard={isGrey} /> : null}
			</Wrapper>
		);
	},
);
